import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Badge, ErrorState, Skeleton, EmptyState } from '@vta/ui';
import type { Program } from '@vta/types';
import { apiClient, ApiError } from '../lib/api-client.ts';
import { OgMeta } from '../lib/og-meta.tsx';
import {
  EventDivisionsList,
  type EventDivisionTeamRow,
} from '../features/events/EventDivisionsList.tsx';
import { formatProgramLabel, programBadgeVariant } from '../lib/format.ts';

interface EventResponse {
  event: {
    id: string;
    eventCode: string;
    name: string;
    program: Program;
    season: string;
    startDate: string;
    endDate: string;
    venue: string | null;
    city: string | null;
    region: string | null;
    country: string | null;
  };
  teams: EventDivisionTeamRow[];
}

export const Route = createFileRoute('/event/$eventCode')({
  loader: async ({ params }) => {
    try {
      return await apiClient.get<EventResponse>(`/api/events/${encodeURIComponent(params.eventCode)}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  component: EventRoute,
});

function EventRoute() {
  const { eventCode } = Route.useParams();
  const initial = Route.useLoaderData() as EventResponse | null;

  const query = useQuery<EventResponse | null>({
    queryKey: ['event', eventCode],
    initialData: initial,
    queryFn: ({ signal }) =>
      apiClient
        .get<EventResponse>(`/api/events/${encodeURIComponent(eventCode)}`, { signal })
        .catch((err) => {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }),
  });

  if (query.isLoading) return <EventSkeleton />;
  if (query.isError) {
    return (
      <main className="container mx-auto py-10">
        <ErrorState title="Could not load event" retry={() => query.refetch()} />
      </main>
    );
  }

  const data = query.data;
  if (!data) {
    return (
      <main className="container mx-auto py-10">
        <OgMeta
          title="Event not found"
          description="No event with that code is currently catalogued."
          pathname={`/event/${eventCode}`}
        />
        <EmptyState title="Event not found" description="The event code may be wrong or not yet synced." />
      </main>
    );
  }

  const ev = data.event;
  const range = formatRange(ev.startDate, ev.endDate);
  const location = [ev.venue, ev.city, ev.region, ev.country].filter(Boolean).join(', ');

  return (
    <>
      <OgMeta
        title={ev.name}
        description={[range, location].filter(Boolean).join(' — ') || `VEX event ${ev.eventCode}.`}
        pathname={`/event/${ev.eventCode}`}
      />
      <main className="container mx-auto flex flex-col gap-8 py-8 md:py-12">
        <header className="flex flex-col gap-3 border-b border-border-subtle pb-6">
          <p className="text-2xs uppercase tracking-[0.3em] text-fg-subtle">Event</p>
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <h1 className="font-display text-3xl text-fg sm:text-4xl">{ev.name}</h1>
            <Badge variant={programBadgeVariant(ev.program)}>
              {formatProgramLabel(ev.program, 'short')}
            </Badge>
          </div>
          <p className="text-sm text-fg-muted">
            {[range, location].filter(Boolean).join(' · ')}
          </p>
          <p className="font-mono text-2xs uppercase tracking-[0.18em] text-fg-subtle">
            {ev.eventCode} · {ev.season}
          </p>
        </header>

        <EventDivisionsList teams={data.teams} />
      </main>
    </>
  );
}

function formatRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
  const fmt = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' });
  if (s.toDateString() === e.toDateString()) return fmt.format(s);
  return `${fmt.format(s)} – ${fmt.format(e)}`;
}

function EventSkeleton() {
  return (
    <main className="container mx-auto flex flex-col gap-6 py-10">
      <Skeleton className="h-12 w-2/3" />
      <Skeleton className="h-5 w-1/2" />
      <Skeleton className="h-96 w-full" />
    </main>
  );
}
